import React, { useContext, useState } from "react";
import { ReportParamsContext } from "../../context/ReportParamsContext";
import { getPhotoCapture } from "../../API";

// Classify a rep's avg relative to company avg
function repStatus(avg, companyAvg, threshold) {
  if (companyAvg === 0) return "neutral";
  if (avg >= companyAvg) return "good";
  if (avg >= companyAvg * threshold) return "warn";
  return "bad";
}

function StatusIcon({ status }) {
  if (status === "good") return <span className="text-success fw-bold me-1">●</span>;
  if (status === "warn") return <span className="text-warning fw-bold me-1">●</span>;
  if (status === "bad")  return <span className="text-danger fw-bold me-1">●</span>;
  return <span className="text-muted me-1">●</span>;
}

function AvgBar({ avg, companyAvg }) {
  // Bar is relative to 2× company avg so bars are comparable
  const cap = companyAvg > 0 ? companyAvg * 2 : 10;
  const repWidth  = Math.min((avg / cap) * 100, 100);
  const avgLine   = Math.min((companyAvg / cap) * 100, 100);

  return (
    <div className="d-flex align-items-center gap-2" style={{ minWidth: 200 }}>
      <span className="fw-semibold" style={{ minWidth: 38, textAlign: "right" }}>
        {avg.toFixed(1)}
      </span>
      <div className="position-relative flex-grow-1" style={{ height: 10 }}>
        <div className="progress w-100" style={{ height: 10, borderRadius: 4 }}>
          <div
            className={`progress-bar ${avg >= companyAvg ? "bg-success" : avg >= companyAvg * 0.7 ? "bg-warning" : "bg-danger"}`}
            style={{ width: `${repWidth}%`, borderRadius: 4 }}
          />
        </div>
        {/* Company avg reference line */}
        {companyAvg > 0 && (
          <div
            className="position-absolute top-0"
            style={{
              left: `${avgLine}%`,
              width: 2,
              height: 10,
              background: "#333",
              borderRadius: 1,
            }}
            title={`Group avg: ${companyAvg.toFixed(1)}`}
          />
        )}
      </div>
      {companyAvg > 0 && (
        <span className="text-muted" style={{ fontSize: "0.72rem", whiteSpace: "nowrap" }}>
          avg {companyAvg.toFixed(1)}
        </span>
      )}
    </div>
  );
}

export default function PhotoCaptureReport() {
  const [{ fromDate, toDate, representatives }] = useContext(ReportParamsContext);

  const [flagThreshold, setFlagThreshold] = useState(70);
  const [data,          setData]          = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);
  const [expandedReps,  setExpandedReps]  = useState(new Set());

  const handleGenerate = async () => {
    if (!fromDate || !toDate) { setError("Please select at least one month in the parameters bar."); return; }
    setLoading(true);
    setError(null);
    try {
      const result = await getPhotoCapture({
        fromDate,
        toDate,
        flagThreshold: flagThreshold / 100,
        representativeIds: representatives.map((r) => r.id),
      });
      setData(result);
      setExpandedReps(new Set());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleRep = (repId) =>
    setExpandedReps((prev) => {
      const next = new Set(prev);
      next.has(repId) ? next.delete(repId) : next.add(repId);
      return next;
    });

  const flaggedCount = data?.reps.filter((r) => r.flagged).length ?? 0;

  return (
    <div className="flex-grow-1 overflow-auto">
      <div className="container-fluid py-4 px-4">

        <div className="card shadow-sm mb-4">
          <div className="card-body p-3">
            <div className="d-flex align-items-center gap-4 flex-wrap">
              <div>
                <label className="form-label small fw-medium mb-1 d-block">
                  Flag threshold (% of group avg)
                </label>
                <div className="d-flex align-items-center gap-2">
                  <input
                    type="range" className="form-range" min={10} max={100} step={5}
                    value={flagThreshold}
                    onChange={(e) => setFlagThreshold(Number(e.target.value))}
                    style={{ width: 140 }}
                  />
                  <span className="badge bg-secondary">{flagThreshold}%</span>
                </div>
              </div>
              <div className="ms-auto">
                <button className="btn btn-primary" onClick={handleGenerate} disabled={loading}>
                  {loading ? "Loading…" : "Generate Report"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {data && (
          <>
            {/* Summary cards */}
            <div className="row g-3 mb-3">
              <div className="col-sm-3">
                <div className="card text-center shadow-sm h-100">
                  <div className="card-body py-3">
                    <div className="fs-3 fw-bold">{data.companyAvg.toFixed(1)}</div>
                    <div className="text-muted small">Group avg photos / call</div>
                  </div>
                </div>
              </div>
              <div className="col-sm-3">
                <div className="card text-center shadow-sm h-100">
                  <div className="card-body py-3">
                    <div className="fs-3 fw-bold">{data.companyTotalPhotos}</div>
                    <div className="text-muted small">
                      Total photos ({data.companyTotalVisits} visits)
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-sm-3">
                <div className="card text-center shadow-sm h-100">
                  <div className="card-body py-3">
                    <div className="fs-4 fw-bold text-success">
                      {data.reps[0]?.repName ?? "—"}
                    </div>
                    <div className="text-muted small">
                      Top ({data.reps[0]?.avgPerCall.toFixed(1) ?? 0} photos/call)
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-sm-3">
                <div className={`card text-center shadow-sm h-100 ${flaggedCount > 0 ? "border-danger" : ""}`}>
                  <div className="card-body py-3">
                    <div className={`fs-3 fw-bold ${flaggedCount > 0 ? "text-danger" : "text-success"}`}>
                      {flaggedCount}
                    </div>
                    <div className="text-muted small">
                      Rep{flaggedCount !== 1 ? "s" : ""} below {flagThreshold}% of avg
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Legend */}
            <div className="d-flex align-items-center gap-4 mb-3 small text-muted">
              <span>
                <span className="text-success fw-bold">●</span> At or above group avg
              </span>
              <span>
                <span className="text-warning fw-bold">●</span> Between {flagThreshold}% and 100% of avg
              </span>
              <span>
                <span className="text-danger fw-bold">●</span> Below {flagThreshold}% of avg (flagged)
              </span>
              <span>
                <span style={{ display: "inline-block", width: 2, height: 10, background: "#333", verticalAlign: "middle" }} className="me-1" />
                = group average reference
              </span>
            </div>

            {data.reps.length === 0 ? (
              <div className="alert alert-info">No visits found for this period.</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-bordered mb-0" style={{ fontSize: "0.85rem" }}>
                  <thead className="table-dark">
                    <tr>
                      <th style={{ width: 24 }} />
                      <th>Representative</th>
                      <th style={{ minWidth: 260 }}>Avg photos / call</th>
                      <th className="text-end">Total photos</th>
                      <th className="text-end">Visits</th>
                      <th className="text-end">Manufacturers</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.reps.map((rep) => {
                      const status = repStatus(rep.avgPerCall, data.companyAvg, data.flagThreshold);
                      return (
                        <React.Fragment key={rep.repId}>
                          <tr
                            className={rep.flagged ? "table-danger" : ""}
                            style={{ cursor: "pointer" }}
                            onClick={() => toggleRep(rep.repId)}
                          >
                            <td className="text-center fw-bold" style={{ userSelect: "none" }}>
                              {expandedReps.has(rep.repId) ? "▾" : "▸"}
                            </td>
                            <td className="fw-semibold">
                              <StatusIcon status={status} />
                              {rep.repName || "(Unknown)"}
                            </td>
                            <td>
                              <AvgBar avg={rep.avgPerCall} companyAvg={data.companyAvg} />
                            </td>
                            <td className="text-end">{rep.totalPhotos}</td>
                            <td className="text-end">{rep.visitCount}</td>
                            <td className="text-end">{rep.manufacturers.length}</td>
                          </tr>

                          {expandedReps.has(rep.repId) && rep.manufacturers.map((mfr) => (
                            <tr key={mfr.mfrGuid} className="table-light" style={{ fontSize: "0.8rem" }}>
                              <td />
                              <td className="ps-4 text-muted">
                                <StatusIcon status={repStatus(mfr.avgPerCall, mfr.companyAvgPerCall, data.flagThreshold)} />
                                {mfr.mfrName}
                              </td>
                              <td>
                                <AvgBar avg={mfr.avgPerCall} companyAvg={mfr.companyAvgPerCall} />
                              </td>
                              <td className="text-end">{mfr.photoCount}</td>
                              <td />
                              <td />
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
